import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import {
    describeOutcomesWait,
    describeParticipation,
    AgencyAlertKind,
    IAgencyDigest,
    IHistoryProgress,
    IFamilyParticipation,
    loadDirection,
    ParticipationList,
    IOutcomeMetric
} from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK } from './agencyChartTheme';
import { CaseloadPulseChart } from './CaseloadPulseChart';
import { AttentionTrend } from './AttentionTrend';
import { InsightsCard } from './InsightsCard';
import { InsightsInfoTip } from './InsightsInfoTip';
import { OutcomesReport } from './OutcomesReport';
import { OutcomeSummaries } from './OutcomeSummaries';
import { InsightsAskBea } from './InsightsAskBea';
import { BeaSays } from './BeaSays';
import { NeedsHistory } from './NeedsHistory';

const useStyles = createUseStyles({
    figures: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12
    },

    figure: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 2,
        width: '100%',
        padding: '12px 14px',
        borderRadius: 11,
        border: `1px solid ${BRAND.tealEdge}`,
        background: BRAND.tealWash,
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',
        transition: 'border-color 140ms ease',

        '&:hover, &:focus-visible': {
            borderColor: BRAND.deepTeal
        }
    },

    figureValue: {
        fontSize: 26,
        fontWeight: 600,
        lineHeight: 1.1,
        color: BRAND.deepTeal,
        fontVariantNumeric: 'tabular-nums'
    },

    figureUnit: {
        fontSize: 15,
        fontWeight: 500
    },

    figureLabel: {
        fontSize: 12.5,
        lineHeight: 1.4,
        color: CHART_INK.label
    },

    figureMore: {
        marginTop: 3,
        fontSize: 11.5,
        fontWeight: 500,
        color: BRAND.deepTeal
    },
});

const PARTICIPATION_EXPLAINER =
    'Counted from the record over the last seven days, not from a survey: a client is participating '
    + 'when their family has put at least one observation on file in that week. Families join by '
    + 'scanning the code left in the home, so there is no app to install and nothing to log into. '
    + 'Ask me who is quiet and I will read their records back to you.';

const ACTIVITY_EXPLAINER =
    'Every observation the caseload recorded, bucketed by the week it describes and colored by what '
    + 'kind it was. Open a bar to read that week rather than to filter the page. If a week looks odd, '
    + 'ask me about it and I will go through what was written.';

/**
 * The leadership half of the tab: what the caseload can evidence about itself.
 *
 * Outcomes lead, because they are the reason anyone opens this. Everything under them is the working
 * behind the figures: how much of the book is actually contributing, what the week looked like, and
 * what is on file. The order is deliberate, and it is the opposite of the order these were built in.
 *
 * The one rule the whole page is built around: nothing here claims Innerhive kept a client home or
 * out of hospital. Nobody has measured the counterfactual. Every figure is a statement about what is
 * on the record, and each explains exactly what it counted.
 */
export const InsightsValueLens: FC<{
    digest: IAgencyDigest;
    /** A seven-day count, real at any caseload age: on a young book a low figure is the onboarding gap. */
    participation: IFamilyParticipation;
    history: IHistoryProgress;
    outcomes: IOutcomeMetric[];
    selectedKind?: AgencyAlertKind;
    onOpenOutcome: (metric: IOutcomeMetric) => void;
    onOpenParticipation: (which: ParticipationList) => void;
    onOpenWeek: (weekStartsOn: string) => void;
    onSelectKind: (kind: AgencyAlertKind) => void;
    onAskBea: (question: string) => void;
}> = ({
    digest, participation, history, outcomes, selectedKind,
    onOpenOutcome, onOpenParticipation, onOpenWeek, onSelectKind, onAskBea
}) => {
    const classes = useStyles();

    const direction = loadDirection(digest);

    const shownOutcomes = outcomes.filter(metric => history.hasEnough || !metric.definition.needsHistory);
    const waitingOutcomes = outcomes.filter(metric => !shownOutcomes.includes(metric));

    return (
        <>
            <InsightsAskBea onAsk={onAskBea} />

            <InsightsCard
                heading="Outcomes Report"
                blurb="What the record shows for the caseload. Open any figure for the clients behind it and exactly how it is counted."
                action={(
                    <InsightsInfoTip label="How these figures are counted">
                        <OutcomeSummaries definitions={outcomes.map(metric => metric.definition)} />
                    </InsightsInfoTip>
                )}
            >
                <OutcomesReport
                    metrics={shownOutcomes}
                    pending={waitingOutcomes.length > 0
                        ? describeOutcomesWait({ waiting: waitingOutcomes.map(metric => metric.title), history })
                        : undefined}
                    onOpen={onOpenOutcome}
                />
            </InsightsCard>

            <InsightsCard
                heading="Family Participation"
                blurb="How much of the book had a family contributing in the last seven days."
                action={<InsightsInfoTip label="How participation is counted">{PARTICIPATION_EXPLAINER}</InsightsInfoTip>}
            >
                <div className={classes.figures} data-id="family-participation">
                    <button
                        type="button"
                        className={classes.figure}
                        data-id="participation-rate"
                        onClick={() => onOpenParticipation('participating')}
                    >
                        <span className={classes.figureValue}>{participation.participationPercent}%</span>
                        <span className={classes.figureLabel}>of clients had a family reporting this week</span>
                        <span className={classes.figureMore}>See who &rsaquo;</span>
                    </button>

                    <button
                        type="button"
                        className={classes.figure}
                        data-id="participation-count"
                        onClick={() => onOpenParticipation('participating')}
                    >
                        <span className={classes.figureValue}>
                            {participation.participatingCount}
                            <span className={classes.figureUnit}> / {participation.clientCount}</span>
                        </span>
                        <span className={classes.figureLabel}>clients with family signal on file</span>
                        <span className={classes.figureMore}>See who &rsaquo;</span>
                    </button>

                    <button
                        type="button"
                        className={classes.figure}
                        data-id="participation-signal"
                        onClick={() => onOpenParticipation('signal')}
                    >
                        <span className={classes.figureValue}>{participation.signalCount}</span>
                        <span className={classes.figureLabel}>observations recorded this week</span>
                        <span className={classes.figureMore}>See who &rsaquo;</span>
                    </button>

                    <button
                        type="button"
                        className={classes.figure}
                        data-id="participation-quiet"
                        onClick={() => onOpenParticipation('quiet')}
                    >
                        <span className={classes.figureValue}>{participation.quietCount}</span>
                        <span className={classes.figureLabel}>
                            {participation.quietCount === 1 ? 'client to onboard' : 'clients to onboard'}, no family signal this week
                        </span>
                        <span className={classes.figureMore}>See who &rsaquo;</span>
                    </button>
                </div>

                <BeaSays panel="participation">
                    {describeParticipation({ participation, unreadableCount: digest.unreadableCount })}
                </BeaSays>
            </InsightsCard>

            <InsightsCard
                heading="Caseload Activity"
                blurb={history.hasEnough
                    ? 'Observations recorded each week, by kind. Open a bar to read that week.'
                    : 'Observations recorded each week, by kind.'}
                action={history.hasEnough ? <InsightsInfoTip label="What this chart shows">{ACTIVITY_EXPLAINER}</InsightsInfoTip> : undefined}
            >
                <NeedsHistory history={history} panel="activity">
                    <CaseloadPulseChart
                        weekly={digest.weekly}
                        selectedKind={selectedKind}
                        onOpenWeek={onOpenWeek}
                        onSelectKind={onSelectKind}
                    />

                    <BeaSays panel="activity">
                        I sorted every observation into the week it describes. The coral band is what I would
                        look at first: those are the weeks where somebody moved against their own baseline.
                    </BeaSays>
                </NeedsHistory>
            </InsightsCard>

            <InsightsCard
                heading="Week Over Week"
                blurb={history.hasEnough
                    ? `Earlier weeks against recent ones, across the whole caseload. Currently ${direction}.`
                    : 'Earlier weeks against recent ones, across the whole caseload.'}
            >
                <NeedsHistory history={history} panel="week-over-week">
                    <AttentionTrend digest={digest} />

                    <BeaSays panel="week-over-week">
                        {direction === 'rising'
                            ? 'The caseload is generating more to look at than it was earlier in the window. That is usually more families reporting rather than more going wrong, but it is worth knowing which.'
                            : direction === 'easing'
                                ? 'Quieter than earlier in the window. Worth checking that families are still reporting before reading it as everyone settling.'
                                : 'Holding about level with earlier in the window.'}
                    </BeaSays>
                </NeedsHistory>
            </InsightsCard>
        </>
    );
};
